import { BoxProps } from '@mui/material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import React, { useEffect, useState } from 'react';
import { CartItem as CartItemType } from 'generated/graphql';
import { ProductHelper } from 'helpers/ProductHelper';
import { useCart } from 'hooks/useCart';
import useNotificationBar from 'hooks/useNotificationBar';

import { useDebounce } from 'usehooks-ts';
import PublicQuantity from 'components/PublicQuantity';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { ProductListCard } from 'components/common/ProductListCard';

type CartItemProps = BoxProps & {
    item: CartItemType;
};

export function CartItem({ item }: CartItemProps) {
    const { product } = item;
    const {
        quantity,
        child_id,
    } = item;
    const productName = product.name;
    const productId = product.parentWrapperId ?? product.id;
    const childId = child_id ?? undefined;

    const { addToCart, deleteFromCart } = useCart();
    const { showError, showSuccess } = useNotificationBar();
    const { t } = useServerTranslation();
    const [updatedQuantity, setUpdatedQuantity] = useState(quantity);

    const updateQuantity = (newQuantity: number) => {
        setUpdatedQuantity(newQuantity);
    };

    const debouncedQuantity = useDebounce(updatedQuantity, 500);

    useEffect(() => {
        if (debouncedQuantity !== quantity) {
            addToCart(
                {
                    productId,
                    childId,
                    quantity: debouncedQuantity,
                },
            );
        }
    }, [addToCart, childId, debouncedQuantity, productId, quantity]);

    const onDelete = async () => {
        try {
            await deleteFromCart(productId, childId);

            showSuccess(t('shopping-cart.actions.delete-from-cart-success', { productName }));
        } catch (e) {
            showError(t('shopping-cart.actions.delete-from-cart-fail', { productName }));
        }
    };

    const selectedProduct = ProductHelper.getSelectedProduct(product, childId) ?? product;

    const [firstImage] = selectedProduct.images;

    return (
        <ProductListCard
            quantityComponent={() => (
                <PublicQuantity
                    value={updatedQuantity}
                    onChange={updateQuantity}
                />
            )}
            onDeleteComponent={() => (
                <DeleteForeverOutlinedIcon
                    onClick={onDelete}
                />
            )}
            productImage={firstImage?.url}
            productName={productName}
            sku={selectedProduct.sku}
            productPrices={selectedProduct}
        />
    );
}
